// SNIGJ - Forgot Password Page
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { t } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Scale, Mail, Sun, Moon, Globe, AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const ForgotPasswordPage = () => {
  const { language, updateLanguage } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/forgot-password', { email });
      setSuccess(true);
      toast.success(language === 'fr' 
        ? 'Instructions envoyées par email' 
        : 'Reset instructions sent to your email');
    } catch (err) {
      // Even on error, show success message to prevent email enumeration
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex flex-col">
        {/* Top bar */}
        <div className="flex justify-between items-center gap-3 p-4">
          <Link 
            to="/login" 
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">
              {language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateLanguage(language === 'fr' ? 'en' : 'fr')}
              data-testid="forgot-lang-toggle"
            >
              <Globe className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'EN' : 'FR'}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="forgot-theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Logo */}
        <div className="flex justify-center py-6">
          <div className="w-16 h-16 rounded bg-yellow-400 flex items-center justify-center">
            <Scale className="w-10 h-10 text-slate-900" />
          </div>
        </div>

        {/* Form container */}
        <div className="flex-1 flex items-start justify-center px-4 sm:px-8">
          <Card className="w-full max-w-md border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="font-heading text-xl sm:text-2xl">
                {language === 'fr' ? 'Mot de passe oublié' : 'Forgot Password'}
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">
                {language === 'fr' 
                  ? 'Entrez votre email pour recevoir un lien de réinitialisation'
                  : 'Enter your email to receive a reset link'}
              </CardDescription>
            </CardHeader>
            
            <CardContent className="pt-2">
              {success ? (
                <div className="text-center space-y-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-2">
                      {language === 'fr' ? 'Email envoyé !' : 'Email Sent!'}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {language === 'fr' 
                        ? `Si un compte existe avec l'adresse ${email}, vous recevrez un email avec les instructions pour réinitialiser votre mot de passe.`
                        : `If an account exists with ${email}, you will receive an email with instructions to reset your password.`}
                    </p>
                  </div>
                  <div className="pt-4 space-y-3">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => {
                        setSuccess(false);
                        setEmail('');
                      }}
                    >
                      {language === 'fr' ? 'Essayer une autre adresse' : 'Try another email'}
                    </Button>
                    <Link to="/login" className="block">
                      <Button className="w-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200">
                        {language === 'fr' ? 'Retour à la connexion' : 'Back to login'}
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <>
                  {error && (
                    <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-center gap-2 text-red-700 dark:text-red-400 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{error}</span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">{t(language, 'auth.email')}</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="nom@justice.gouv.cd"
                          className="pl-10"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          data-testid="forgot-email"
                        />
                      </div>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
                      disabled={loading}
                      data-testid="forgot-submit"
                    >
                      {loading 
                        ? (language === 'fr' ? 'Envoi en cours...' : 'Sending...') 
                        : (language === 'fr' ? 'Envoyer le lien' : 'Send Reset Link')}
                    </Button>
                  </form>

                  <p className="text-center text-sm text-muted-foreground mt-6">
                    {language === 'fr' ? 'Vous vous souvenez de votre mot de passe ?' : 'Remember your password?'}{' '}
                    <Link to="/login" className="text-yellow-600 hover:underline font-medium">
                      {language === 'fr' ? 'Se connecter' : 'Login'}
                    </Link>
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="p-4 text-center text-xs text-muted-foreground">
          © {new Date().getFullYear()} GROUPE KAZAM
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
